import { ed25519 } from '@noble/curves/ed25519';
import { hmac } from '@noble/hashes/hmac';
import { sha256, sha512 } from '@noble/hashes/sha2';
import { base58 } from '@scure/base';
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
export const PATHS = {
    solana: "m/44'/501'/0'/0'",
    gajumaru: "m/44'/457'/0'/0'/0'",
};
export function newRecoveryPhrase() {
    return generateMnemonic(wordlist, 128);
}
export function isRecoveryPhrase(phrase) {
    return validateMnemonic(phrase.trim().toLowerCase().split(/\s+/).join(' '), wordlist);
}
export function seedFromPhrase(phrase) {
    return mnemonicToSeedSync(phrase.trim().toLowerCase().split(/\s+/).join(' '));
}
/** SLIP-0010 for ed25519: every step hardened, as the standard requires. */
function slip10(seed, path) {
    let I = hmac(sha512, new TextEncoder().encode('ed25519 seed'), seed);
    let key = I.slice(0, 32);
    let chain = I.slice(32);
    for (const part of path.split('/').slice(1)) {
        if (!part.endsWith("'"))
            throw new Error(`ed25519 paths must be hardened: ${path}`);
        const index = Number(part.slice(0, -1)) + 0x80000000;
        const data = new Uint8Array(37);
        data.set(key, 1);
        new DataView(data.buffer).setUint32(33, index);
        I = hmac(sha512, chain, data);
        key = I.slice(0, 32);
        chain = I.slice(32);
    }
    return key;
}
export function deriveKey(seed, chain) {
    const secret = slip10(seed, PATHS[chain]);
    const publicKey = ed25519.getPublicKey(secret);
    return { chain, secret, publicKey, address: addressOf(chain, publicKey) };
}
export function addressOf(chain, publicKey) {
    if (chain === 'solana')
        return base58.encode(publicKey);
    // aeternity: base58check, the checksum being the first four bytes of a double sha256.
    const check = sha256(sha256(publicKey)).slice(0, 4);
    const payload = new Uint8Array(36);
    payload.set(publicKey);
    payload.set(check, 32);
    return `ak_${base58.encode(payload)}`;
}
export function sign(key, message) {
    return ed25519.sign(message, key.secret);
}
export function verify(publicKey, message, signature) {
    try {
        return ed25519.verify(signature, message, publicKey);
    }
    catch {
        return false;
    }
}
/** The public key inside an address, for verifying a registration. */
export function publicKeyOf(chain, address) {
    if (chain === 'solana') {
        const key = base58.decode(address);
        if (key.length !== 32)
            throw new Error('Not a Solana address');
        return key;
    }
    if (!address.startsWith('ak_'))
        throw new Error('Not a Gajumaru address');
    const payload = base58.decode(address.slice(3));
    if (payload.length !== 36)
        throw new Error('Not a Gajumaru address');
    const key = payload.slice(0, 32);
    const check = sha256(sha256(key)).slice(0, 4);
    if (check.some((b, i) => b !== payload[32 + i]))
        throw new Error('Not a Gajumaru address');
    return key;
}
/** What a person signs to bind an address to their Telegram account. */
export function registrationMessage(telegramId, chain, address) {
    return new TextEncoder().encode(`xchange:register:${telegramId}:${chain}:${address}`);
}
/**
 * What a person signs to sign in from a browser: a one-time nonce the server
 * issued for this address, with its expiry, so a captured signature opens
 * nothing later and nothing else.
 */
export function loginMessage(address, nonce, expiresAt) {
    return new TextEncoder().encode(`xchange:login:${address}:${nonce}:${expiresAt}`);
}
/** Zero a secret once it has done its job. */
export function wipe(...buffers) {
    for (const b of buffers)
        b.fill(0);
}
//# sourceMappingURL=keys.js.map